import React, { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import SolicitudServicioModal from '../ordenes/SolicitudServicioModal';
import OrdenDetalleModal from '../ordenes/OrdenDetalleModal';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Monitor, 
  FileText, 
  TrendingUp,
  Calendar,
  Wrench,
  User,
  AlertTriangle,
  Activity,
  BarChart3,
  Zap,
  PlusCircle
} from 'lucide-react';

export default function ClienteDashboard() {
  const [mostrarModalSolicitud, setMostrarModalSolicitud] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [data, setData] = useState({
    horas: null,
    ordenes: [],
    equipos: [],
    loading: true,
    error: null
  });

  // Función helper para convertir horas decimales a formato HH:MM
  const formatearHoras = (horasDecimal) => {
    if (horasDecimal === null || horasDecimal === undefined || isNaN(horasDecimal)) {
      return '0:00';
    }
    const horas = Math.floor(horasDecimal);
    const minutos = Math.round((horasDecimal - horas) * 60);
    return `${horas}:${minutos.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      console.log('🚀 Cargando dashboard del cliente...');
      
      const [horasRes, ordenesRes, equiposRes] = await Promise.all([
        http.get('/cliente/mis-horas'),
        http.get('/cliente/mis-ordenes'),
        http.get('/cliente/mis-equipos')
      ]);

      console.log('✅ Datos cargados:', {
        horas: horasRes.data,
        ordenes: ordenesRes.data?.length,
        equipos: equiposRes.data?.length
      });

      // Debug específico para órdenes
      if (Array.isArray(ordenesRes.data) && ordenesRes.data.length > 0) {
        console.log('🔍 Primera orden recibida:', ordenesRes.data[0]);
        console.log('🔍 Campos disponibles en orden:', Object.keys(ordenesRes.data[0]));
      }

      setData({
        horas: horasRes.data,
        ordenes: Array.isArray(ordenesRes.data) ? ordenesRes.data : [],
        equipos: Array.isArray(equiposRes.data) ? equiposRes.data : [],
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      let errorMsg;
      
      if (error.response?.status === 401) {
        errorMsg = 'Su sesión ha expirado. Por favor vuelva a iniciar sesión.';
      } else if (error.response?.status === 403) {
        errorMsg = 'No tiene permisos para acceder a esta sección.';
      } else {
        errorMsg = error.response?.data?.detail || 'Error cargando datos';
      }
      
      setData(prev => ({
        ...prev,
        loading: false,
        error: errorMsg
      }));
    }
  };

  const calcularPorcentajeUso = () => {
    if (!data.horas?.resumen) return 0;
    const { horas_trabajadas, horas_contratadas } = data.horas.resumen;
    const trabajadas = parseFloat(horas_trabajadas) || 0;
    const contratadas = parseFloat(horas_contratadas) || 1;
    return Math.min(Math.round((trabajadas / contratadas) * 100), 100);
  };

  const obtenerEstadisticasEquipos = () => {
    const estados = data.equipos.reduce((acc, equipo) => {
      const estado = equipo.estado || 'operativo';
      acc[estado] = (acc[estado] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: data.equipos.length,
      operativo: estados.operativo || 0,
      mantenimiento: estados.mantenimiento || 0,
      fuera_servicio: estados.fuera_servicio || 0
    };
  };

  const obtenerEstadisticasOrdenes = () => {
    const estados = data.ordenes.reduce((acc, orden) => {
      const estado = orden.estado || 'pendiente';
      acc[estado] = (acc[estado] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: data.ordenes.length,
      pendiente: estados.pendiente || 0,
      en_proceso: estados.en_proceso || 0,
      completada: estados.completada || 0,
      cancelada: estados.cancelada || 0
    };
  };

  const obtenerOrdenesRecientes = () => {
    return data.ordenes
      .sort((a, b) => {
        // Usar fecha_creacion o fecha como fallback
        const fechaA = a.fecha_creacion || a.fecha;
        const fechaB = b.fecha_creacion || b.fecha;
        
        if (!fechaA && !fechaB) return 0;
        if (!fechaA) return 1;
        if (!fechaB) return -1;
        
        return new Date(fechaB) - new Date(fechaA);
      })
      .slice(0, 5);
  };

  if (data.loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            <span className="text-gray-600 font-medium">Cargando dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Error al cargar dashboard</h3>
              <p className="text-red-700 mt-1">{data.error}</p>
            </div>
          </div>
          <button
            onClick={cargarDatos}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const porcentajeUso = calcularPorcentajeUso();
  const statsEquipos = obtenerEstadisticasEquipos();
  const statsOrdenes = obtenerEstadisticasOrdenes();
  const ordenesRecientes = obtenerOrdenesRecientes();

  return (
  <div className="min-h-screen bg-gray-50 p-4 md:p-6">
  <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Panel de Cliente
          </h1>
          <p className="text-gray-600">
            Resumen de sus servicios, equipos y estado de cuenta
          </p>
        </div>

        {/* Estadísticas principales */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {/* Horas contratadas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <span className="text-2xl font-bold text-emerald-600">
                {porcentajeUso}%
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Uso de Horas</h3>
            <p className="text-sm text-gray-600 mb-3">
              {formatearHoras(data.horas?.resumen?.horas_trabajadas || 0)} de {formatearHoras(data.horas?.resumen?.horas_contratadas || 0)}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${porcentajeUso}%` }}
              ></div>
            </div>
          </div>

          {/* Órdenes de servicio */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-2xl font-bold text-green-600">
                {statsOrdenes.total}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Órdenes Totales</h3>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                {statsOrdenes.pendiente} Pendientes
              </span>
              <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded">
                {statsOrdenes.en_proceso} En proceso
              </span>
            </div>
          </div>

          {/* Equipos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Monitor className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-2xl font-bold text-purple-600">
                {statsEquipos.total}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Equipos</h3>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                {statsEquipos.operativo} Operativos
              </span>
              {statsEquipos.mantenimiento > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">
                  {statsEquipos.mantenimiento} Mantenimiento
                </span>
              )}
            </div>
          </div>

          {/* Estado general */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Activity className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-600">Activo</span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Estado de Cuenta</h3>
            <p className="text-sm text-gray-600">
              Cliente {data.horas?.resumen?.tipo_cliente || 'Regular'}
            </p>
          </div>
        </div>

        {/* Contenido principal */}
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Órdenes recientes */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Órdenes Recientes
                </h2>
                <a 
                  href="#"
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('navigate', { detail: 'mis_ordenes' }));
                  }}
                >
                  Ver todas
                </a>
              </div>
            </div>
            <div className="p-6">
              {ordenesRecientes.length > 0 ? (
                <div className="space-y-4">
                  {ordenesRecientes.map((orden) => (
                    <div 
                      key={orden._id || orden.id} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => setOrdenSeleccionada(orden)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Wrench className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {orden.titulo || orden.equipo_descripcion || `Orden #${orden.numero_orden || orden.numero || 'Sin número'}`}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {orden.fecha_creacion || orden.fecha ? 
                              new Date(orden.fecha_creacion || orden.fecha).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              }) : 'Sin fecha'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          orden.estado === 'completada' 
                            ? 'bg-green-100 text-green-800'
                            : orden.estado === 'en_proceso'
                            ? 'bg-emerald-100 text-emerald-800'
                            : orden.estado === 'cancelada'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {orden.estado === 'en_proceso' ? 'En Proceso' : 
                           orden.estado === 'completada' ? 'Completada' :
                           orden.estado === 'cancelada' ? 'Cancelada' :
                           'Pendiente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay órdenes recientes</h3>
                  <p className="text-gray-600">Sus órdenes de servicio aparecerán aquí</p>
                </div>
              )}
            </div>
          </div>

          {/* Panel lateral */}
          <div className="space-y-6">
            {/* Resumen del período */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Período Actual
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mes:</span>
                  <span className="font-medium">{data.horas?.periodo?.nombre_mes || 'Actual'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Horas disponibles:</span>
                  <span className="font-medium text-emerald-600">
                    {formatearHoras((data.horas?.resumen?.horas_contratadas || 0) - (data.horas?.resumen?.horas_trabajadas || 0))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Equipos activos:</span>
                  <span className="font-medium text-green-600">{statsEquipos.operativo}</span>
                </div>
              </div>
            </div>

            {/* Acciones rápidas */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                Acciones Rápidas
              </h3>
              <div className="space-y-3">
                <button 
                  onClick={() => setMostrarModalSolicitud(true)}
                  className="w-full p-3 text-left bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors group shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1 bg-white/20 rounded">
                      <PlusCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="block font-medium text-white">Solicitar Servicio</span>
                      <span className="text-xs text-emerald-100">Crear nueva orden de soporte</span>
                    </div>
                  </div>
                </button>

                <button 
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'mis_ordenes' }))}
                  className="w-full p-3 text-left bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-emerald-900">Ver Mis Órdenes</span>
                  </div>
                </button>
                <button 
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'mis_equipos' }))}
                  className="w-full p-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Monitor className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-900">Ver Mis Equipos</span>
                  </div>
                </button>
                <button 
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'mis_agendamientos' }))}
                  className="w-full p-3 text-left bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-900">Mis Agendamientos</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Solicitud */}
      {mostrarModalSolicitud && (
        <SolicitudServicioModal
          equipos={data.equipos}
          onClose={() => setMostrarModalSolicitud(false)}
          onSuccess={() => {
            cargarDatos(); // Recargar datos para mostrar la nueva orden
            // Opcional: Mostrar notificación de éxito
          }}
        />
      )}

      {/* Modal de Detalle de Orden */}
      {ordenSeleccionada && (
        <OrdenDetalleModal
          orden={ordenSeleccionada}
          onClose={() => setOrdenSeleccionada(null)}
          onUpdate={cargarDatos}
        />
      )}
    </div>
  );
}